import { api } from '../lib/api.js';
import type {
  EmployeeCreateInput,
  EmployeeUpdate,
  EmployeeSelfUpdate,
  EmployeeListQuery,
  EmployeeEnrollmentStatusSchema,
  WorkforceType,
} from '@attendance/shared';
import type { z } from 'zod';

/** FIELD face-enrollment workflow state surfaced read-only on the list/detail
 * (NOT_ENROLLED | PENDING_SYNC | ENROLLED). System-controlled — never written
 * from the web. */
export type EmployeeEnrollmentStatus = z.infer<typeof EmployeeEnrollmentStatusSchema>;

export interface EmployeeListItem {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  photoUrl: string | null;
  joiningDate: string;
  employmentStatus: string;
  /** FIELD face-enrollment workflow state (read-only). Older API builds may
   * omit it, so reads must tolerate `undefined`. */
  enrollmentStatus?: EmployeeEnrollmentStatus;
  /** Worker vs Staff (Employee.workforceType) — the axis the roster group
   * filter, the report views and the ERP export's population split all key off.
   * On the list projection, not just the detail. Optional because older API
   * builds omit it. */
  workforceType?: WorkforceType;
  /** Free-text nationality, on the list projection so the roster can column it.
   *  Optional, not `string | null`: an older API build omits the field
   *  ENTIRELY, which is a different fact from "this person has no nationality
   *  on file" — and only `undefined` can carry the difference. Readers that
   *  print it must treat both as "not set" but must not print a zero-ish
   *  placeholder that claims the roster was read when it wasn't. */
  nationality?: string | null;
  department: { id: string; name: string } | null;
  designation: { id: string; title: string } | null;
  // Login-less "worker" employees (imported via the ERP master) have no User
  // row, so `user` can be null. Every read site must null-guard.
  user: { id: string; email: string; phone: string | null; role: string; status: string } | null;
}

export interface EmployeeDetail extends EmployeeListItem {
  middleName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  // `nationality` is inherited from EmployeeListItem now — the list projection
  // carries it, so the detail type must not re-declare it as non-optional.
  maritalStatus: string | null;
  personalEmail: string | null;
  emergencyName: string | null;
  emergencyPhone: string | null;
  emergencyRelation: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  emirate: string | null;
  country: string | null;
  passportNumber: string | null;
  passportExpiry: string | null;
  emiratesIdNumber: string | null;
  emiratesIdExpiry: string | null;
  visaNumber: string | null;
  visaExpiry: string | null;
  laborCardNumber: string | null;
  laborCardExpiry: string | null;
  /**
   * ERP payroll code — the CLOCKCARDSBATCH `PRL` column. A STRING, never a
   * number: "002" and "2" are different payrolls, so any input bound to this
   * must be a text field (`type="text"`), never `type="number"`. `null` means
   * "not on any ERP payroll" — the export excludes those workers and lists them
   * on screen rather than dropping them silently.
   */
  erpPayrollNumber: string | null;
  confirmationDate: string | null;
  probationEndDate: string | null;
  contractType: string;
  // Both are in the API's DETAIL_SELECT and were simply missing here, so the
  // detail page had to widen the type locally to read them.
  contractStartDate: string | null;
  contractEndDate: string | null;
  // Legal entity ("company entity") the worker is tagged to. Scalar id only —
  // the detail endpoint selects `legalEntityId`, not the nested relation.
  legalEntityId: string | null;
  // Labour supplier, for AGENCY-employed workers. Scalar id, same as above:
  // callers resolve the name via laborSuppliersApi.
  supplierId: string | null;
  // "Attendance access" — one value: what this person does every morning.
  // Optional because an API build predating the column omits it, in which case
  // the form derives it from the two deprecated axes below.
  attendanceProfile?:
    | 'SELF_ASSIGNED'
    | 'SELF_ANY_SITE'
    | 'SELF_ANYWHERE'
    | 'FACE_DEVICE';
  /** @deprecated read only as the fallback for `attendanceProfile`. */
  attendanceLocation?: 'ASSIGNED_SITE' | 'ANY_WORKSITE' | 'ANYWHERE';
  /** @deprecated read only as the fallback for `attendanceProfile`. */
  attendanceMethod?: 'APP_LOGIN' | 'KIOSK_FACE' | 'FIELD_FACE';
  /**
   * Per-worker working start, "HH:MM" in the company timezone, or `null` to
   * follow the company default (Settings → `defaultWorkday.shiftStartTime`).
   * `null` is a real value here, not "unknown": it is what the form sends to
   * hand a worker back to the company rule, and what the rollup reads as "no
   * override". Optional only because older API builds omit the key entirely.
   */
  workStartTime?: string | null;
  /**
   * Per-worker working end, same rules as `workStartTime` above: "HH:MM" or
   * `null` to follow `defaultWorkday.shiftEndTime`. It is what makes leaving
   * early measurable — without it the last check-out had nothing to be compared
   * against — and like the start it moves no pay figure, only a flag.
   */
  workEndTime?: string | null;
  managerId: string | null;
  manager: {
    id: string;
    firstName: string;
    lastName: string;
    displayName: string | null;
  } | null;
  basicSalary: string | null;
  housingAllowance: string | null;
  transportAllowance: string | null;
  otherAllowance: string | null;
  totalSalary: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankIban: string | null;
  /**
   * Number of ACTIVE (192-d) face templates enrolled for this employee.
   * Surfaced by the detail/me endpoints so the FieldFaceEnroll badge reflects
   * persisted state on load rather than starting at 0 (P3-2).
   */
  fieldTemplateCount?: number;
}

export interface EmployeeWorksiteAssignment {
  id: string;
  worksiteId: string;
  employeeId: string;
  isPrimary: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
  worksite: {
    id: string;
    name: string;
    code: string | null;
    type: string;
    isActive: boolean;
  };
}

export interface ListMeta {
  total: number;
  page: number;
  limit: number;
}

/** One row of the bulk-import dry-run/preview. */
export interface BulkImportPreviewRow {
  /** 1-indexed row number including the header row. */
  row: number;
  status: 'ok' | 'skip' | 'error';
  reason?: string;
  email: string;
  name: string;
  role: string;
  department: string | null;
  designation: string | null;
}

export interface BulkImportPreview {
  rows: BulkImportPreviewRow[];
  summary: { total: number; ok: number; skip: number; error: number };
}

export interface BulkImportResult {
  created: number;
  skipped: number;
  errors: Array<{ row: number; reason: string }>;
}

export const employeesApi = {
  list: (query: Partial<EmployeeListQuery> = {}) =>
    api.get<{ data: EmployeeListItem[]; meta: ListMeta }>('/employees', {
      params: query,
    }),
  get: (id: string) =>
    api.get<{ data: EmployeeDetail }>(`/employees/${id}`).then((r) => r.data),
  me: () => api.get<{ data: EmployeeDetail }>('/employees/me').then((r) => r.data),
  /**
   * Count of active SITE_WORKER records — feeds the Settings → Features turn-off
   * warning so the admin sees how many workers (and their history) get hidden
   * before turning the "Site workers" pillar OFF. ADMIN/HR only.
   */
  siteWorkerCount: () =>
    api
      .get<{ data: { count: number } }>('/employees/site-worker-count')
      .then((r) => r.data.count),
  create: (input: EmployeeCreateInput) =>
    api.post<{ data: EmployeeDetail }>('/employees', input).then((r) => r.data),
  update: (id: string, input: EmployeeUpdate) =>
    api.patch<{ data: EmployeeDetail }>(`/employees/${id}`, input).then((r) => r.data),
  updateSelf: (input: EmployeeSelfUpdate) =>
    api.patch<{ data: EmployeeDetail }>('/employees/me', input).then((r) => r.data),
  remove: (id: string) => api.delete<{ data: { ok: true } }>(`/employees/${id}`),
  /**
   * Replace the employee's profile photo.
   *
   * Multipart, field name `photo` — NOT a base64 data URI in a JSON body. The
   * sister app's admin upload does exactly that and writes the result into a
   * `STRING(255)` column, so every photo it has ever "saved" is a truncated
   * prefix that renders as nothing. `Employee.photoUrl` holds a server-issued
   * path; the bytes travel as bytes.
   */
  uploadPhoto: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    // `Content-Type: null` DELETES the header for this request, and both halves
    // of that matter.
    //
    // Leaving it alone is not an option: the client's default is
    // `application/json`, and axios's request transform sees a JSON content
    // type with a FormData body and quietly runs `formDataToJSON` on it. The
    // file becomes `{"photo":{}}` — a 200-shaped request carrying nothing.
    // (Verified against axios 1.16: `lib/defaults/index.js`.)
    //
    // Nor can we name the type ourselves, the way the bulk-import calls above
    // do: a multipart body is unparseable without its `boundary`, and only the
    // platform knows the boundary it just generated. Deleting the header is
    // what makes it fill one in.
    return api
      .post<{ data: EmployeeDetail }>(`/employees/${id}/photo`, formData, {
        headers: { 'Content-Type': null },
      })
      .then((r) => r.data);
  },
  deletePhoto: (id: string) =>
    api.delete<{ data: EmployeeDetail }>(`/employees/${id}/photo`).then((r) => r.data),
  /**
   * Path of the authenticated photo endpoint. Not a URL you can put in an
   * `<img src>` directly — there is no static file serving in this app, so the
   * bytes come back through the bearer-token client (see `useAuthedImage`).
   */
  photoPath: (id: string) => `/employees/${id}/photo`,
  getWorksites: (id: string) =>
    api
      .get<{ data: EmployeeWorksiteAssignment[] }>(`/employees/${id}/worksites`)
      .then((r) => r.data),
  /**
   * Dry-run a CSV/.xlsx upload: validates every row and returns a per-row
   * verdict for the preview table. Commits nothing.
   */
  bulkImportPreview: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<{ data: BulkImportPreview }>('/employees/bulk-import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  /**
   * Commit a previously-previewed CSV/.xlsx upload. The server re-validates and
   * persists only the valid rows in one transaction; duplicates are skipped.
   */
  bulkImportCommit: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<{ data: BulkImportResult }>('/employees/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  /**
   * THE NATIONALITIES ACTUALLY ON FILE, commonest first.
   *
   * A lookup, not a page of records: the report workspace's nationality filter
   * offered a hardcoded list of demonyms (Emirati, Egyptian, Filipino) against
   * a column the ERP fills with country names (INDIA, PAKISTAN), so no option
   * it showed could ever match a row. See the service for why the list comes
   * from the whole population rather than from the rows on screen.
   */
  nationalities: () =>
    api
      .get<{ data: { value: string; count: number }[] }>('/employees/nationalities')
      .then((r) => r.data),
};
